/**
 * Per-route SEO metadata + JSON-LD builders.
 * Keyword map (one primary theme per URL, no cannibalisation):
 *   /                                  web + app development company for US clients
 *   /services                          software development services (hub)
 *   /services/website-development      website development company
 *   /services/web-app-development      web application development company
 *   /services/mobile-app-development   mobile app development company
 *   /services/custom-software-...      custom software development company
 *   /services/ecommerce-website-...    ecommerce website development
 *   /services/ai-solutions             AI development company / AI solutions
 *   /website-development-company-<city> city landing pages
 *   data/landings.js                   app city pages, Shopify, app cost
 */
import { SITE, SERVICES } from '@/data/site';
import { CITIES } from '@/data/cities';
import { landingByPath } from '@/data/landings';
import { workByPath } from '@/data/work';

export const SITE_URL = 'https://niodelta.us';
export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const AREAS = ['Texas', 'Florida', 'California', 'New York', 'Georgia', 'Arizona'];

const BRAND = 'NioDelta';

const STATIC_PAGES = {
  '/': {
    title: `Web & App Development Company for US Businesses | ${BRAND}`,
    description:
      'NioDelta builds websites, mobile apps, ecommerce stores, custom software and AI tools for US businesses in Texas, Florida, California, New York and beyond. Fixed quotes from $2,900.',
  },
  '/services': {
    title: `Web, App, Ecommerce & AI Development Services | ${BRAND}`,
    description:
      'Website development, web apps, mobile apps, custom software, ecommerce and AI solutions — one senior studio serving US clients on US hours. Fixed scope or retainer.',
    breadcrumb: [['Services', '/services']],
  },
  '/work': {
    title: `Our Work: Website, App & Software Case Studies | ${BRAND}`,
    description:
      'Live projects built by NioDelta: Staffly, Shiksha CRM, Foodysh, Rupantaram and ForexPips. Apps, websites and software, each with screenshots and what we built.',
    breadcrumb: [['Work', '/work']],
  },
  '/about': {
    title: `About NioDelta — Software Studio Serving US Clients, Est. 2017`,
    description:
      'NioDelta is a software and web development studio founded in 2017. Twelve designers and engineers in India building for founders and operators across the United States.',
    breadcrumb: [['About', '/about']],
  },
  '/website-development-cost': {
    title: `Website Development Cost in the US (2026) — Real Prices`,
    description:
      'Website, ecommerce and app development prices in the US — market rates vs NioDelta packages. Business websites from $2,900. Fixed quotes, no US sales tax added.',
    breadcrumb: [['Pricing', '/website-development-cost']],
  },
  '/contact': {
    title: `Contact NioDelta — Web & App Developers for US Clients`,
    description:
      'Talk to a partner, not a salesperson. Email or message NioDelta for website, app, ecommerce or AI projects anywhere in the US. Reply within 1 business day.',
    breadcrumb: [['Contact', '/contact']],
  },
  '/policy': {
    title: `Privacy Policy | ${BRAND}`,
    description:
      'How NioDelta collects, uses, shares and protects personal data on niodelta.us: enquiries, cookies, Google Ads measurement, retention and your rights.',
    breadcrumb: [['Privacy Policy', '/policy']],
  },
  '/tos': {
    title: `Terms of Service | ${BRAND}`,
    description:
      'Terms for using niodelta.us: acceptable use, indicative pricing and quotes, intellectual property, third-party links, liability limits and governing law.',
    breadcrumb: [['Terms of Service', '/tos']],
  },
};

const SERVICE_META = {
  'website-development': {
    title: `Website Development Company for US Businesses | ${BRAND}`,
    description:
      'Custom website design and development for businesses across the US. Fast, mobile-first, SEO-ready sites on React and Next.js. Business websites from $2,900.',
  },
  'web-app-development': {
    title: `Web Application Development Company for US Businesses | ${BRAND}`,
    description:
      'Custom web app development for US companies: dashboards, portals, SaaS and admin panels with secure auth, realtime data and clean APIs. Built to scale, from $9,900.',
  },
  'mobile-app-development': {
    title: `Mobile App Development Company — Android & iOS | ${BRAND}`,
    description:
      'iOS and Android app development for US businesses. Flutter and React Native apps with offline sync, push notifications and App Store / Play Store launch. From $12,000.',
  },
  'custom-software-development': {
    title: `Custom Software Development Company for US Businesses | ${BRAND}`,
    description:
      'Bespoke software for US businesses: CRM, ERP add-ons, quoting tools, ops dashboards and integrations that replace spreadsheets for good. Fixed quotes from $9,900.',
  },
  'ecommerce-website-development': {
    title: `Ecommerce Website Development for US Brands | ${BRAND}`,
    description:
      'Shopify and custom ecommerce website development for US brands. Stripe, Shop Pay and sales-tax-ready checkout, fast product pages and retention built in.',
  },
  'ai-solutions': {
    title: `AI Development Company for US Businesses — Agents, RAG | ${BRAND}`,
    description:
      'Production AI solutions for US businesses: chatbots, RAG search, AI agents and workflow automation with evals, guardrails and cost control. From $6,500.',
  },
};

export function getPageSeo(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (STATIC_PAGES[path]) return { path, ...STATIC_PAGES[path] };

  const svc = path.match(/^\/services\/([\w-]+)$/);
  const service = svc && SERVICES.find((s) => s.slug === svc[1]);
  if (service && SERVICE_META[svc[1]]) {
    return {
      path,
      ...SERVICE_META[svc[1]],
      breadcrumb: [['Services', '/services'], [service.title, path]],
      service,
    };
  }

  const city = CITIES.find((c) => c.path === path);
  if (city) {
    return {
      path,
      title: city.seoTitle,
      description: city.seoDescription,
      breadcrumb: [[`Website Development ${city.name}`, path]],
      city,
    };
  }

  const project = workByPath(path);
  if (project) {
    return {
      path,
      title: project.seoTitle,
      description: project.seoDescription,
      breadcrumb: [['Work', '/work'], [project.name, path]],
      project,
    };
  }

  const landing = landingByPath(path);
  if (landing) {
    return {
      path,
      title: landing.seoTitle,
      description: landing.seoDescription,
      breadcrumb: landing.crumbs,
      landing,
    };
  }

  return {
    path,
    title: `Page not found | ${BRAND}`,
    description: 'This page does not exist.',
    noindex: true,
  };
}

export const canonicalUrl = (path) => (path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}/`);

const ORG_ID = `${SITE_URL}/#organization`;

export function organizationSchema() {
  return {
    '@type': 'ProfessionalService',
    '@id': ORG_ID,
    name: BRAND,
    alternateName: 'NioDelta Web Studio',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo.png`,
    image: OG_IMAGE,
    email: SITE.email,
    telephone: SITE.phone,
    foundingDate: '2017',
    priceRange: '$1,200 – $45,000',
    description:
      'Website, web app, mobile app, ecommerce and AI development studio serving businesses across the United States.',
    areaServed: [
      ...AREAS.map((name) => ({ '@type': 'State', name })),
      { '@type': 'Country', name: 'United States' },
    ],
    knowsAbout: SERVICES.map((s) => s.title),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: SITE.phone,
      email: SITE.email,
      contactType: 'sales',
      areaServed: 'US',
      availableLanguage: ['English', 'Spanish'],
    },
  };
}

function breadcrumbSchema(items) {
  const all = [['Home', '/'], ...items];
  return {
    '@type': 'BreadcrumbList',
    itemListElement: all.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: canonicalUrl(path),
    })),
  };
}

function serviceSchema({ name, description, path, areas }) {
  return {
    '@type': 'Service',
    name,
    description,
    url: canonicalUrl(path),
    provider: { '@id': ORG_ID },
    areaServed: areas.map((a) => ({ '@type': 'State', name: a })),
  };
}

export function buildJsonLd(seo) {
  const graph = [organizationSchema()];
  if (seo.path === '/') {
    graph.push({
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: BRAND,
      publisher: { '@id': ORG_ID },
      inLanguage: 'en-US',
    });
  }
  if (seo.breadcrumb) graph.push(breadcrumbSchema(seo.breadcrumb));
  if (seo.service) {
    graph.push(
      serviceSchema({ name: seo.service.title, description: seo.description, path: seo.path, areas: AREAS })
    );
  }
  if (seo.city) {
    graph.push(
      serviceSchema({
        name: `Website & App Development in ${seo.city.name}`,
        description: seo.description,
        path: seo.path,
        areas: [seo.city.name],
      })
    );
  }
  if (seo.project) {
    graph.push({
      '@type': 'CreativeWork',
      name: seo.project.name,
      url: seo.project.url,
      description: seo.project.summary,
      about: seo.project.sector,
      creator: { '@id': ORG_ID },
      mainEntityOfPage: canonicalUrl(seo.path),
    });
  }
  if (seo.landing?.schemaName) {
    graph.push(
      serviceSchema({
        name: seo.landing.schemaName,
        description: seo.description,
        path: seo.path,
        areas: seo.landing.areas,
      })
    );
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
